// The Mail workspace's Outbox tab (Session AUD, email system Phase 5, Item 2; Director decision DE-1, one-click batch
// approval). Every job-stage notice owed to a customer NOW (a call-out received; an engineer booked, or the booking
// changed, for a call-out, repair or technician visit: D230, D231), derived live by the server
// (server/src/modules/customerNotices/outbox.ts) with the email each would send.
//
// NOTHING GOES UNTIL A PERSON PRESSES SEND (D-MAIL-1, the AHR rule). The batch is: tick the notices, "Send selected", and the
// branded confirm (the BRANDED CONFIRM rule) first asks the server's preview, which re-checks every notice against the
// fingerprint this page showed and sends nothing, and names exactly who each one goes to (To and Cc) and its subject, and
// separately any it would NOT send and why. Only the confirm's own Send button sends, and only the notices the preview said
// would go. The server sends them one by one; each result is shown, a failure never hiding the rest.
//
// "Do not send" is a two-step ConfirmButton and is reversible: an Undo stays on the row until the list is next reloaded.
// The email is shown as the customer will see it in a SANDBOXED frame (sandbox="" with srcDoc: no script runs), the
// Company signature card's pattern; the HTML is the server's own.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../../../lib/api";
import type { OutboxDismissal, OutboxNotice, OutboxPreviewItem, OutboxSendResult } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { fmtLondonDateTime } from "../../../lib/mailWorkspace";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { EmptyState } from "../../../components/EmptyState";
import { Loader } from "../../../components/Loader";
import { formatDateLong } from "../../../lib/format";
import { ModalShell, MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "../../../components/ModalShell";
import { Button } from "../../../components/Button";

type OutboxCounts = { total: number; sendable: number; failed: number; blocked: number };

const SMALL_BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-inset hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";
const AMBER_NOTE = "rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text-soft)]";
const DANGER_NOTE = "rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong";

/** What the notice is about, in words: a call-out's reported time, or a booking's day. */
function aboutText(n: OutboxNotice): string {
  if (n.kind === "callout_received") return `Reported ${fmtLondonDateTime(n.about)}`;
  return `Booked for ${formatDateLong(n.about)}`;
}

function placeText(n: Pick<OutboxNotice, "siteName" | "eleNumber">): string {
  return n.eleNumber ? `${n.siteName} (${n.eleNumber})` : n.siteName;
}

/** A row's label, for the confirm and the results. */
function noticeLabel(n: Pick<OutboxNotice, "kindLabel" | "siteName" | "eleNumber">): string {
  return `${n.kindLabel} · ${placeText(n)}`;
}

/** Can this notice be ticked for sending? */
function canSelect(n: OutboxNotice): boolean {
  return n.sendable && !!n.fingerprint && n.decision?.status !== "sending";
}

export function OutboxTab({ onChanged }: { onChanged: () => void }) {
  const [items, setItems] = useState<OutboxNotice[] | null>(null);
  const [counts, setCounts] = useState<OutboxCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // Rows dismissed since the last reload: key -> the decision id the Undo restores.
  const [dismissed, setDismissed] = useState<Map<string, string>>(() => new Map());
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ key: string; message: string } | null>(null);
  const [confirmFor, setConfirmFor] = useState<OutboxNotice[] | null>(null);
  const [results, setResults] = useState<Array<{ result: OutboxSendResult; label: string }> | null>(null);
  // Notices dismissed in the last 30 days, so a dismissal can be undone after the screen has moved on (RULE 1).
  const [recent, setRecent] = useState<OutboxDismissal[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.mail.outbox.list();
      setItems(res.items);
      setCounts(res.counts);
      // Best-effort: the list still works if the dismissed list cannot be read.
      void Promise.resolve()
        .then(() => api.mail.outbox.dismissed())
        .then((d) => setRecent(d.dismissed))
        .catch(() => setRecent([]));
      setDismissed(new Map());
      // Keep only the ticks that still point at a notice that can be sent.
      setSelected((prev) => {
        const ok = new Set(res.items.filter(canSelect).map((i) => i.key));
        return new Set([...prev].filter((k) => ok.has(k)));
      });
    } catch (err) {
      setError(friendlyApiError(err, "Could not load the Outbox."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selectable = useMemo(() => (items ?? []).filter((i) => canSelect(i) && !dismissed.has(i.key)), [items, dismissed]);
  const chosen = useMemo(() => selectable.filter((i) => selected.has(i.key)), [selectable, selected]);
  const allTicked = selectable.length > 0 && chosen.length === selectable.length;

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allTicked ? new Set() : new Set(selectable.map((i) => i.key)));
  }

  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function dismiss(n: OutboxNotice) {
    setRowBusy(n.key);
    setRowError(null);
    try {
      const res = await api.mail.outbox.dismiss(n.key);
      setDismissed((prev) => new Map(prev).set(n.key, res.noticeId));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(n.key);
        return next;
      });
      onChanged();
    } catch (err) {
      setRowError({ key: n.key, message: friendlyApiError(err, "Could not mark this notice as not to be sent.") });
    } finally {
      setRowBusy(null);
    }
  }

  async function restore(key: string, decisionId: string, fallback: string) {
    setRowBusy(key);
    setRowError(null);
    try {
      await api.mail.outbox.restore(decisionId);
      onChanged();
      await load();
    } catch (err) {
      setRowError({ key, message: friendlyApiError(err, fallback) });
    } finally {
      setRowBusy(null);
    }
  }

  async function afterSend(sent: OutboxSendResult[], targets: OutboxNotice[]) {
    const labels = new Map(targets.map((t) => [t.key, noticeLabel(t)]));
    setResults(sent.map((r) => ({ result: r, label: labels.get(r.key) ?? "A notice" })));
    setConfirmFor(null);
    setSelected(new Set());
    onChanged();
    await load();
  }

  const intro = (
    <div className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface px-4 py-3 text-sm text-ele-body">
      <p className="text-ele-strong">Emails to customers about their call-outs and bookings.</p>
      <p className="mt-1">
        A notice appears here when a call-out comes in, or when an engineer is booked (or the booking changes) for a call-out,
        repair or technician visit. Nothing goes until someone presses Send. Routine service visits are not here.
      </p>
    </div>
  );

  let body: ReactNode;
  if (error) {
    body = (
      <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
        <p className="text-ele-strong">{error}</p>
        <button type="button" onClick={() => void load()} className={`mt-3 ${SMALL_BTN}`}>
          Retry
        </button>
      </div>
    );
  } else if (!items) {
    body = (
      <Loader label="Loading the Outbox..." rows={5} />
    );
  } else if (items.length === 0) {
    body = (
      <EmptyState
        message="Nothing waiting. Notices appear here when a call-out comes in, or an engineer is booked for a call-out, repair or technician visit."
        hint="Routine service visits never appear here."
      />
    );
  } else {
    body = (
      <>
        <div className="mb-3 flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <label className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
              <input
                type="checkbox"
                checked={allTicked}
                disabled={selectable.length === 0}
                onChange={toggleAll}
                className="h-5 w-5 accent-ele-green"
              />
              Select all that can be sent
            </label>
            {counts && (
              <span className="text-xs text-ele-body" data-testid="outbox-counts">
                {counts.total} waiting · {counts.sendable} can be sent
                {counts.failed > 0 ? ` · ${counts.failed} failed` : ""}
                {counts.blocked > 0 ? ` · ${counts.blocked} cannot be sent` : ""}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={() => void load()} disabled={loading} className={SMALL_BTN}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <Button size="sm" className="max-sm:w-full" disabled={chosen.length === 0} onClick={() => setConfirmFor(chosen)}>
              Send selected ({chosen.length})
            </Button>
          </div>
        </div>
        <ul className="space-y-2" aria-label="Notices waiting to be sent">
          {items.map((n) => (
            <OutboxRow
              key={n.key}
              notice={n}
              ticked={selected.has(n.key)}
              expanded={expanded.has(n.key)}
              dismissedId={dismissed.get(n.key) ?? null}
              busy={rowBusy === n.key}
              error={rowError?.key === n.key ? rowError.message : null}
              onToggle={() => toggle(n.key)}
              onExpand={() => toggleExpanded(n.key)}
              onDismiss={() => void dismiss(n)}
              onUndo={(id) => void restore(n.key, id, "Could not undo. Refresh the list and try again.")}
              onClearFailure={(id) => void restore(n.key, id, "Could not clear the failure.")}
              onSendAgain={() => setConfirmFor([n])}
            />
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className="min-w-0" data-testid="outbox-tab">
      {intro}
      {results && <SendResults results={results} onClose={() => setResults(null)} />}
      {body}
      {recent.length > 0 && (
        <RecentDismissals
          rows={recent}
          busyId={rowBusy}
          error={rowError}
          onRestore={(id) => void restore(id, id, "Could not bring that notice back. Refresh the list and try again.")}
        />
      )}
      {confirmFor && (
        <OutboxSendConfirm
          targets={confirmFor}
          onCancel={() => setConfirmFor(null)}
          onSent={(r) => void afterSend(r, confirmFor)}
          onUncertain={() => {
            onChanged();
            void load();
          }}
        />
      )}
    </div>
  );
}

function OutboxRow({
  notice: n,
  ticked,
  expanded,
  dismissedId,
  busy,
  error,
  onToggle,
  onExpand,
  onDismiss,
  onUndo,
  onClearFailure,
  onSendAgain,
}: {
  notice: OutboxNotice;
  ticked: boolean;
  expanded: boolean;
  dismissedId: string | null;
  busy: boolean;
  error: string | null;
  onToggle: () => void;
  onExpand: () => void;
  onDismiss: () => void;
  onUndo: (decisionId: string) => void;
  onClearFailure: (decisionId: string) => void;
  onSendAgain: () => void;
}) {
  const failed = n.decision?.status === "failed" ? n.decision : null;
  const sending = n.decision?.status === "sending";
  const selectable = canSelect(n) && !dismissedId;
  const label = noticeLabel(n);

  return (
    <li
      className={`min-w-0 rounded-ele border bg-ele-surface px-3 py-3 ${ticked ? "border-ele-green" : "border-ele-purple-accent"} ${dismissedId ? "opacity-70" : ""}`}
      data-testid={`outbox-row-${n.key}`}
      aria-label={label}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex w-11 shrink-0 justify-center sm:w-6">
          {selectable ? (
            <label className="flex min-h-11 min-w-11 items-center justify-center sm:min-h-0 sm:min-w-0">
              <input type="checkbox" checked={ticked} onChange={onToggle} aria-label={`Select ${label}`} className="h-5 w-5 accent-ele-green" />
            </label>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="shrink-0 rounded-ele border border-ele-purple-accent px-2 py-0.5 font-menu text-xs tracking-wide text-ele-strong">{n.kindLabel}</span>
            <span className="min-w-0 truncate text-xs text-ele-body">{aboutText(n)}</span>
          </div>
          <p className="mt-1 truncate font-heading text-ele-strong" title={`${n.customerName ?? "No customer on record"} · ${placeText(n)}`}>
            {n.customerName ?? "No customer on record"} · {placeText(n)}
          </p>
          <p className="mt-0.5 text-xs text-ele-body [overflow-wrap:anywhere]" data-testid={`outbox-to-${n.key}`}>
            To <span className="text-ele-strong">{n.to ?? "no address on record"}</span>
            {n.cc ? (
              <>
                {" "}
                · Cc <span className="text-ele-strong">{n.cc}</span>
              </>
            ) : null}
          </p>
          <p className="mt-0.5 truncate text-xs text-ele-body" title={n.subject}>
            Subject: <span className="text-ele-strong">{n.subject}</span>
          </p>
          {n.kind === "engineer_updated" && n.previous && (
            <p className="mt-0.5 text-xs text-ele-body" data-testid={`outbox-previous-${n.key}`}>
              The customer was last told: {n.previous.engineerName ?? "an engineer"}
              {n.previous.bookedFor ? ` on ${formatDateLong(n.previous.bookedFor)}` : ""}.
            </p>
          )}
          {n.blockedReason && (
            <p className={`mt-2 ${AMBER_NOTE}`} data-testid={`outbox-blocked-${n.key}`}>
              Not sendable: {n.blockedReason}
            </p>
          )}
          {sending && <p className={`mt-2 ${AMBER_NOTE}`}>This notice is being sent right now.</p>}
          {failed && !dismissedId && (
            <div className={`mt-2 ${DANGER_NOTE}`} role="status" data-testid={`outbox-failed-${n.key}`}>
              <p>Sending failed: {failed.failure ?? "the email did not go."}</p>
              <p className="mt-1 text-xs text-ele-body">It can be sent again. Clearing the failure keeps the notice waiting here.</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                {canSelect(n) && (
                  <button type="button" onClick={onSendAgain} disabled={busy} className={SMALL_BTN}>
                    Send again
                  </button>
                )}
                <button type="button" onClick={() => onClearFailure(failed.id)} disabled={busy} className={SMALL_BTN}>
                  Clear the failure
                </button>
              </div>
            </div>
          )}
          {dismissedId && (
            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ele-body" role="status">
              <span>This notice will not be sent.</span>
              <button type="button" onClick={() => onUndo(dismissedId)} disabled={busy} className={SMALL_BTN}>
                {busy ? "Undoing..." : "Undo"}
              </button>
            </p>
          )}
          {error && (
            <p role="alert" className={`mt-2 ${DANGER_NOTE}`}>
              {error}
            </p>
          )}
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button type="button" onClick={onExpand} aria-expanded={expanded} className={SMALL_BTN}>
              {expanded ? "Hide the email" : "Show the email"}
            </button>
            {!dismissedId && !sending && (
              <ConfirmButton
                onConfirm={onDismiss}
                label="Do not send"
                confirmLabel="Press again: do not send"
                busy={busy}
                busyLabel="Saving..."
                className={SMALL_BTN}
              />
            )}
          </div>
          {expanded && (
            <div className="mt-3 min-w-0 space-y-3" data-testid={`outbox-email-${n.key}`}>
              <div>
                <p className="mb-1 font-menu text-xs uppercase tracking-widest text-ele-blue">As the customer will see it</p>
                <iframe
                  title={`The email: ${n.subject}`}
                  sandbox=""
                  srcDoc={n.html}
                  className="h-[420px] w-full rounded-ele border border-ele-hairline bg-white"
                />
              </div>
              <div>
                <p className="mb-1 font-menu text-xs uppercase tracking-widest text-ele-blue">Plain text version</p>
                <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-xs text-ele-strong [overflow-wrap:anywhere]">
                  {n.text}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

type PreviewState = { kind: "loading" } | { kind: "ready"; items: OutboxPreviewItem[] } | { kind: "error"; message: string };

/**
 * The branded confirm for the batch. Asks the server's preview first (nothing sent), names every recipient and subject,
 * and lists separately any notice that would not go. Its Send sends only the notices the preview said would go.
 */
export function OutboxSendConfirm({
  targets,
  onCancel,
  onSent,
  onUncertain,
}: {
  targets: OutboxNotice[];
  onCancel: () => void;
  onSent: (results: OutboxSendResult[]) => void;
  /** The send request itself failed: some may have gone, so the list must be read again. */
  onUncertain: () => void;
}) {
  const [preview, setPreview] = useState<PreviewState>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const byKey = useMemo(() => new Map(targets.map((t) => [t.key, t])), [targets]);
  const keysSig = targets.map((t) => `${t.key}:${t.fingerprint ?? ""}`).join("|");

  useEffect(() => {
    let live = true;
    setPreview({ kind: "loading" });
    api.mail.outbox
      .previewSend(targets.filter((t) => t.fingerprint).map((t) => ({ key: t.key, fingerprint: t.fingerprint! })))
      .then((res) => live && setPreview({ kind: "ready", items: res.items }))
      .catch((err) => live && setPreview({ kind: "error", message: friendlyApiError(err, "Could not check who these notices go to.") }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysSig]);

  const going = preview.kind === "ready" ? preview.items.filter((p) => p.wouldSend) : [];
  const notGoing = preview.kind === "ready" ? preview.items.filter((p) => !p.wouldSend) : [];
  const canSend = !busy && preview.kind === "ready" && going.length > 0;

  async function send() {
    const items = going
      .map((p) => ({ key: p.key, fingerprint: byKey.get(p.key)?.fingerprint ?? null }))
      .filter((i): i is { key: string; fingerprint: string } => !!i.fingerprint);
    if (items.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.mail.outbox.send(items);
      onSent(res.results);
    } catch (err) {
      setError(
        `${friendlyApiError(err, "The send could not be confirmed.")} Some notices may already have gone, so the list has been read again: check it before sending again.`,
      );
      onUncertain();
    } finally {
      setBusy(false);
    }
  }

  const title = targets.length === 1 ? "Send this notice?" : `Send ${targets.length} notices?`;
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onCancel()} closeOnBackdrop width="max-w-lg" panelClassName="p-5 shadow-xl">
      <h2 className="font-heading text-lg text-ele-strong">{title}</h2>
      <p className="mt-2 text-sm text-ele-body">Each one goes as its own email from the office mailbox. An email cannot be recalled once sent.</p>
      {preview.kind === "loading" && (
        <p role="status" className="mt-3 text-sm text-ele-body">
          Checking each notice...
        </p>
      )}
      {preview.kind === "error" && (
        <p role="alert" className={`mt-3 ${DANGER_NOTE}`}>
          {preview.message}
        </p>
      )}
      {preview.kind === "ready" && (
        <>
          {going.length > 0 && (
            <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto" aria-label="Who each notice goes to">
              {going.map((p) => {
                const t = byKey.get(p.key);
                return (
                  <li key={p.key} className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-sm">
                    <p className="truncate text-ele-strong">{p.kindLabel && p.siteName ? `${p.kindLabel} · ${p.siteName}` : t ? noticeLabel(t) : p.key}</p>
                    <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                      To <span className="font-semibold text-ele-strong">{p.to}</span>
                      {p.cc ? (
                        <>
                          {" "}
                          · Cc <span className="font-semibold text-ele-strong">{p.cc}</span>
                        </>
                      ) : null}
                    </p>
                    <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                      Subject: <span className="text-ele-strong">{p.subject}</span>
                    </p>
                    {p.warning ? <p className={`mt-1 ${AMBER_NOTE}`}>Last time: {p.warning}</p> : null}
                  </li>
                );
              })}
            </ul>
          )}
          {notGoing.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-ele-strong">{notGoing.length === 1 ? "This one will not be sent:" : `These ${notGoing.length} will not be sent:`}</p>
              <ul className="mt-1 space-y-2" aria-label="Notices that will not be sent">
                {notGoing.map((p) => {
                  const t = byKey.get(p.key);
                  return (
                    <li key={p.key} className={AMBER_NOTE}>
                      <p className="truncate">{t ? noticeLabel(t) : p.key}</p>
                      <p className="text-xs">{p.message ?? "It cannot be sent as it stands."}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {going.length === 0 && <p className="mt-3 text-sm text-ele-body">Nothing can be sent as it stands. Close this and look at the list again.</p>}
        </>
      )}
      {error && (
        <p role="alert" className={`mt-3 ${DANGER_NOTE}`}>
          {error}
        </p>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onCancel} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Cancel
        </button>
        <button type="button" onClick={() => void send()} disabled={!canSend} className={MODAL_PRIMARY_BTN}>
          {busy ? "Sending..." : preview.kind === "loading" ? "Checking..." : going.length === 0 ? "Send" : going.length === 1 ? "Send 1 notice" : `Send ${going.length} notices`}
        </button>
      </div>
    </ModalShell>
  );
}

function SendResults({ results, onClose }: { results: Array<{ result: OutboxSendResult; label: string }>; onClose: () => void }) {
  const sent = results.filter((r) => r.result.outcome === "sent").length;
  return (
    <div className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface px-4 py-3" role="status" data-testid="outbox-results">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ele-strong">
          {sent} of {results.length} sent.
        </p>
        <button type="button" onClick={onClose} className={SMALL_BTN}>
          Close
        </button>
      </div>
      <ul className="mt-2 space-y-1 text-sm" aria-label="What happened to each notice">
        {results.map(({ result: r, label }) => (
          <li key={r.key} className="min-w-0 [overflow-wrap:anywhere]">
            <span className="text-ele-strong">{label}:</span>{" "}
            {r.outcome === "sent" && <span className="text-ele-green">sent to {r.to}</span>}
            {r.outcome === "sent" && r.note ? <span className="block text-[var(--ele-amber-text-soft)]">{r.note}</span> : null}
            {r.outcome === "failed" && <span className="text-ele-danger">failed: {r.message}</span>}
            {r.outcome === "refused" && <span className="text-[var(--ele-amber-text-soft)]">not sent: {r.message}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecentDismissals({
  rows,
  busyId,
  error,
  onRestore,
}: {
  rows: OutboxDismissal[];
  busyId: string | null;
  error: { key: string; message: string } | null;
  onRestore: (decisionId: string) => void;
}) {
  return (
    <details className="mt-6 rounded-ele border border-ele-hairline bg-ele-surface px-4 py-3" data-testid="outbox-dismissed">
      <summary className="cursor-pointer text-sm text-ele-strong">Not sent on purpose, last 30 days ({rows.length})</summary>
      <p className="mt-2 text-xs text-ele-body">
        Bring one back and it waits in the Outbox again, to be sent or not. A notice that was dismissed is never sent on its own.
      </p>
      <ul className="mt-2 space-y-2" aria-label="Notices not sent on purpose">
        {rows.map((r) => (
          <li key={r.decisionId} className="flex min-w-0 flex-col gap-2 rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="truncate text-ele-strong">
                {r.kindLabel}
                {r.siteName ? ` · ${r.siteName}` : ""}
                {r.eleNumber ? ` (${r.eleNumber})` : ""}
              </p>
              <p className="truncate text-xs text-ele-body">
                {r.customerName ? `${r.customerName} · ` : ""}dismissed {fmtLondonDateTime(r.dismissedAt)}
                {r.dismissedBy ? ` by ${r.dismissedBy}` : ""}
              </p>
              {error?.key === r.decisionId && (
                <p role="alert" className="text-xs text-ele-danger">
                  {error.message}
                </p>
              )}
            </div>
            <button type="button" disabled={busyId === r.decisionId} onClick={() => onRestore(r.decisionId)} className={SMALL_BTN}>
              {busyId === r.decisionId ? "Bringing back..." : "Bring back"}
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}
